// src/components/common/vibe/CreatePoll.js
import React, { useState } from 'react';
import { X, Plus, Trash2, Calendar, Users, Eye } from 'lucide-react';
import vibeService from '../../../services/vibe.service';

const VISIBILITY_OPTIONS = [
    { value: 'all', label: 'Everyone in Company', icon: '🏢' },
    { value: 'department', label: 'My Department', icon: '👥' },
    { value: 'team', label: 'My Team', icon: '🤝' },
];

const RESULT_VISIBILITY_OPTIONS = [
    { value: 'all', label: 'Everyone can see results', icon: '👀' },
    { value: 'voters_only', label: 'Only voters can see', icon: '🗳️' },
    { value: 'after_voting', label: 'After voting', icon: '✅' },
    { value: 'creator_only', label: 'Only me (and admins)', icon: '🔒' },
];

const CreatePoll = ({ isOpen, onClose, onPollCreated, theme }) => {
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        poll_title: '',
        poll_description: '',
        poll_question: '',
        poll_options: ['', ''],
        poll_end_date: '',
        poll_visibility: 'all',
        poll_show_results_to: 'all'
    });

    const handleAddOption = () => {
        if (formData.poll_options.length < 10) {
            setFormData({
                ...formData,
                poll_options: [...formData.poll_options, '']
            });
        }
    };

    const handleRemoveOption = (index) => {
        if (formData.poll_options.length > 2) {
            const newOptions = formData.poll_options.filter((_, i) => i !== index);
            setFormData({ ...formData, poll_options: newOptions });
        }
    };

    const handleOptionChange = (index, value) => {
        const newOptions = [...formData.poll_options];
        newOptions[index] = value;
        setFormData({ ...formData, poll_options: newOptions });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // //console.log('📊 Starting poll creation...', {
        //     title: formData.poll_title,
        //     question: formData.poll_question,
        //     optionsCount: formData.poll_options.length
        // });

        // Validation
        if (!formData.poll_title.trim() || !formData.poll_question.trim()) {
            console.warn('❌ Validation failed: Missing title or question');
            alert('Please provide a title and question for the poll.');
            return;
        }

        const validOptions = formData.poll_options.filter(opt => opt.trim() !== '');
        if (validOptions.length < 2) {
            console.warn('❌ Validation failed: Not enough options', validOptions);
            alert('Please provide at least 2 poll options.');
            return;
        }

        setLoading(true);
        try {
            //console.log('🚀 Sending poll creation request to backend...');

            // Call service directly and refresh feed
            const response = await vibeService.createPoll({
                poll_title: formData.poll_title.trim(),
                poll_description: formData.poll_description.trim() || null,
                poll_question: formData.poll_question.trim(),
                poll_options: validOptions,
                poll_end_date: formData.poll_end_date || null,
                poll_visibility: formData.poll_visibility,
                poll_show_results_to: formData.poll_show_results_to,
                visibility: 'company'
            });

            //console.log('✅ Poll created successfully!', response);
            // //console.log('📢 Poll details:', {
            //     id: response.data?.id,
            //     title: response.data?.poll_title,
            //     is_pinned: response.data?.is_pinned
            // });

            // Show success message
            alert(`✅ Poll "${formData.poll_title}" created successfully! It will appear at the top of the feed.`);

            // Reset form
            setFormData({
                poll_title: '',
                poll_description: '',
                poll_question: '',
                poll_options: ['', ''],
                poll_end_date: '',
                poll_visibility: 'all',
                poll_show_results_to: 'all'
            });

            //console.log('🔄 Refreshing feed...');
            // Notify parent to refresh feed
            if (onPollCreated) {
                await onPollCreated();
                //console.log('✅ Feed refreshed');
            } else {
                console.warn('⚠️ No onPollCreated callback provided');
            }

            onClose();
        } catch (error) {
            console.error('❌ Error creating poll:', error);
            console.error('Error details:', {
                message: error.message,
                response: error.response?.data,
                status: error.response?.status
            });
            alert(`❌ Failed to create poll: ${error.response?.data?.message || error.message || 'Unknown error'}`);
        } finally {
            setLoading(false);
            //console.log('🏁 Poll creation process completed');
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
            <div className={`w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-xl p-6 m-4 ${
                theme === 'dark' ? 'bg-gray-800' : 'bg-white'
            }`}>
                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                    <h2 className={`text-2xl font-bold ${
                        theme === 'dark' ? 'text-white' : 'text-gray-900'
                    }`}>
                        📊 Create Poll
                    </h2>
                    <button
                        onClick={onClose}
                        className={`p-2 rounded-lg ${
                            theme === 'dark' ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                        }`}
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="space-y-4">
                    {/* Title */}
                    <div>
                        <label className={`block text-sm font-medium mb-2 ${
                            theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                        }`}>
                            Poll Title <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            value={formData.poll_title}
                            onChange={(e) => setFormData({ ...formData, poll_title: e.target.value })}
                            placeholder="e.g., Team Lunch Preference"
                            maxLength={200}
                            className={`w-full px-4 py-2 rounded-lg border ${
                                theme === 'dark'
                                    ? 'bg-gray-700 border-gray-600 text-white'
                                    : 'bg-white border-gray-300 text-gray-900'
                            }`}
                        />
                    </div>

                    {/* Description */}
                    <div>
                        <label className={`block text-sm font-medium mb-2 ${
                            theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                        }`}>
                            Description (Optional)
                        </label>
                        <textarea
                            value={formData.poll_description}
                            onChange={(e) => setFormData({ ...formData, poll_description: e.target.value })}
                            placeholder="Brief description of what this poll is about..."
                            rows={2}
                            className={`w-full px-4 py-2 rounded-lg border resize-none ${
                                theme === 'dark'
                                    ? 'bg-gray-700 border-gray-600 text-white'
                                    : 'bg-white border-gray-300 text-gray-900'
                            }`}
                        />
                    </div>

                    {/* Question */}
                    <div>
                        <label className={`block text-sm font-medium mb-2 ${
                            theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                        }`}>
                            Poll Question <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            value={formData.poll_question}
                            onChange={(e) => setFormData({ ...formData, poll_question: e.target.value })}
                            placeholder="e.g., What type of food would you like?"
                            className={`w-full px-4 py-2 rounded-lg border ${
                                theme === 'dark'
                                    ? 'bg-gray-700 border-gray-600 text-white'
                                    : 'bg-white border-gray-300 text-gray-900'
                            }`}
                        />
                    </div>

                    {/* Poll Options */}
                    <div>
                        <label className={`block text-sm font-medium mb-2 ${
                            theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                        }`}>
                            Poll Options (2-10) <span className="text-red-500">*</span>
                        </label>
                        <div className="space-y-2">
                            {formData.poll_options.map((option, index) => (
                                <div key={index} className="flex gap-2">
                                    <input
                                        type="text"
                                        value={option}
                                        onChange={(e) => handleOptionChange(index, e.target.value)}
                                        placeholder={`Option ${index + 1}`}
                                        className={`flex-1 px-4 py-2 rounded-lg border ${
                                            theme === 'dark'
                                                ? 'bg-gray-700 border-gray-600 text-white'
                                                : 'bg-white border-gray-300 text-gray-900'
                                        }`}
                                    />
                                    {formData.poll_options.length > 2 && (
                                        <button
                                            type="button"
                                            onClick={() => handleRemoveOption(index)}
                                            className={`p-2 rounded-lg text-red-500 ${
                                                theme === 'dark' ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                                            }`}
                                        >
                                            <Trash2 className="w-5 h-5" />
                                        </button>
                                    )}
                                </div>
                            ))}
                        </div>
                        {formData.poll_options.length < 10 && (
                            <button
                                type="button"
                                onClick={handleAddOption}
                                className={`mt-2 flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium ${
                                    theme === 'dark'
                                        ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                }`}
                            >
                                <Plus className="w-4 h-4" />
                                Add Option
                            </button>
                        )}
                    </div>

                    {/* End Date */}
                    <div>
                        <label className={`block text-sm font-medium mb-2 ${
                            theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                        }`}>
                            <Calendar className="w-4 h-4 inline mr-1" />
                            End Date (Optional)
                        </label>
                        <input
                            type="datetime-local"
                            value={formData.poll_end_date}
                            onChange={(e) => setFormData({ ...formData, poll_end_date: e.target.value })}
                            className={`w-full px-4 py-2 rounded-lg border ${
                                theme === 'dark'
                                    ? 'bg-gray-700 border-gray-600 text-white'
                                    : 'bg-white border-gray-300 text-gray-900'
                            }`}
                        />
                    </div>

                    {/* Who Can Vote */}
                    <div>
                        <label className={`block text-sm font-medium mb-2 ${
                            theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                        }`}>
                            <Users className="w-4 h-4 inline mr-1" />
                            Who Can Vote
                        </label>
                        <select
                            value={formData.poll_visibility}
                            onChange={(e) => setFormData({ ...formData, poll_visibility: e.target.value })}
                            className={`w-full px-4 py-2 rounded-lg border ${
                                theme === 'dark'
                                    ? 'bg-gray-700 border-gray-600 text-white'
                                    : 'bg-white border-gray-300 text-gray-900'
                            }`}
                        >
                            {VISIBILITY_OPTIONS.map(option => (
                                <option key={option.value} value={option.value}>
                                    {option.icon} {option.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Who Can See Results */}
                    <div>
                        <label className={`block text-sm font-medium mb-2 ${
                            theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                        }`}>
                            <Eye className="w-4 h-4 inline mr-1" />
                            Who Can See Results
                        </label>
                        <select
                            value={formData.poll_show_results_to}
                            onChange={(e) => setFormData({ ...formData, poll_show_results_to: e.target.value })}
                            className={`w-full px-4 py-2 rounded-lg border ${
                                theme === 'dark'
                                    ? 'bg-gray-700 border-gray-600 text-white'
                                    : 'bg-white border-gray-300 text-gray-900'
                            }`}
                        >
                            {RESULT_VISIBILITY_OPTIONS.map(option => (
                                <option key={option.value} value={option.value}>
                                    {option.icon} {option.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Submit Buttons */}
                    <div className="flex gap-3 pt-4">
                        <button
                            type="button"
                            onClick={onClose}
                            className={`flex-1 px-6 py-3 rounded-lg font-medium ${
                                theme === 'dark'
                                    ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                            }`}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            className={`flex-1 px-6 py-3 rounded-lg font-medium text-white ${
                                loading
                                    ? 'bg-blue-400 cursor-not-allowed'
                                    : 'bg-blue-600 hover:bg-blue-700'
                            }`}
                        >
                            {loading ? 'Creating...' : 'Create Poll'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default CreatePoll;
